import React, { useEffect, useState } from "react";
import {
  SpocModel,
  WarehouseModel,
} from "../../../../../erp/inventory/models/warehouse_model";
import {
  WarehouseInventorySpocModel,
  warehouseRoles,
} from "../../../../../erp/inventory/models/inventory_warehouse_spoc_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import EmployeeMultiSearchWidget from "../../../../new_components/emp_multi_search_widget";
import { UserModel } from "../../../../../context_providers/auth/user_model";
import {
  CreateMultiWarehouseSPOCAssigneeAPI,
  UpdateWarehouseSpocAPI,
} from "./warehouse_spoc_mangement_apis";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../../context_providers/modal/loader";

interface Props {
  warehouse?: WarehouseModel;
  spocs?: SpocModel[];
  editData?: WarehouseInventorySpocModel; // 👈 NEW
  onClose: () => void;
  onSaved?: () => void;
}

export const AddWarehouseSpocModal: React.FC<Props> = ({
  onClose,
  editData,
  onSaved,
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState<UserModel[]>([]);
  const [data, setData] = useState<WarehouseInventorySpocModel>({
    warehouse_id: "",
    inventory_spocs: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const disabled = !!editData;

  useEffect(() => {
    if (!editData || !editData.role) return;

    const operatorRows = (editData.role as any)["warehouse operator"] ?? [];

    const inventorySpocs: SpocModel[] = operatorRows
      .map((r: any) => r.inventory_spoc)
      .filter(Boolean);

    const reporting_to_id = operatorRows[0]?.reporting_to_id;
    const reporting_to_name = operatorRows[0]?.reporting_to_name;

    setData({
      warehouse_id: editData.warehouse_id,
      warehouse_name: editData.warehouse_name,
      warehouse: editData.warehouse,
      inventory_spocs: inventorySpocs,
      reporting_to_id,
      reporting_to_name,
    });

    const selected = inventorySpocs.map((s) => ({
      uid: s.uid,
      first_name: s.contact_name?.split(" ")[0] ?? "",
      last_name: s.contact_name?.split(" ")[1] ?? "",
      mobile: s.contact_mobile,
      email: s.contact_email,
      emp_profile: { department: s.department },
    })) as UserModel[];

    setSelectedEmployees(selected);
  }, [editData]);

  const handleSelect = (emp: UserModel) => {
    setSelectedEmployees((prev) => {
      const alreadyExists = prev.some((e) => e.uid === emp.uid);
      if (alreadyExists) return prev;

      const updatedEmployees = [...prev, emp];

      const warehouseSpocs: SpocModel[] = updatedEmployees.map((e) => ({
        uid: e.uid,
        contact_name: `${e.first_name} ${e.last_name ?? ""}`.trim(),
        contact_mobile: e.mobile,
        contact_email: e.email,
        department: e.emp_profile?.department,
      }));

      setData((old) => ({
        ...old,
        inventory_spocs: warehouseSpocs,
      }));

      return updatedEmployees;
    });
  };

  const handleDelete = (uid: string) => {
    setSelectedEmployees((prev) => {
      const updatedEmployees = prev.filter((e) => e.uid !== uid);

      const warehouseSpocs: SpocModel[] = updatedEmployees.map((e) => ({
        uid: e.uid,
        contact_name: `${e.first_name} ${e.last_name ?? ""}`.trim(),
        contact_mobile: e.mobile,
        contact_email: e.email,
        department: e.emp_profile?.department,
      }));

      setData((old) => ({
        ...old,
        inventory_spocs: warehouseSpocs,
      }));

      return updatedEmployees;
    });
  };

  const onSubmit = async (data: WarehouseInventorySpocModel) => {
    try {
      if (!data?.warehouse?.id) {
        showToast({
          type: ToastType.error,
          text: "Select a warehouse first",
        });
        return;
      }

      if (!data.inventory_spocs?.length) {
        showToast({
          type: ToastType.error,
          text: "Select at least one SPOC",
        });
        return;
      }

      setIsSubmitting(true);
      const payload = {
        ...data,
      };

      let response;

      if (disabled) {
        response = await UpdateWarehouseSpocAPI(payload);
      } else {
        response = await CreateMultiWarehouseSPOCAssigneeAPI(payload);
      }

      if (!response?.success) {
        showToast({
          type: ToastType.error,
          text: response?.error || "Failed to save warehouse SPOCs",
        });
        return;
      }

      showToast({
        type: ToastType.success,
        text: "Warehouse SPOCs saved successfully",
      });
      onSaved?.();
      onClose();
    } catch (error: any) {
      console.error("API Error full object:", error);

      showToast({
        type: ToastType.error,
        text: error.message || "Something went wrong",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (disabled) {
      setData((prev) => ({
        ...prev,
        role: "warehouse operator",
      }));
    } else {
      setData((prev) => ({
        ...prev,
        role: "manager",
      }));
    }
  }, [disabled]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4">
      <div
        id="warehouse-management-spoc-assignment-modal"
        className="bg-white w-full max-w-[500px] rounded-lg p-4 space-y-4 wh-modal-xs max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-lg font-semibold">Assign SPOCs to Warehouse</h2>

        {/* SPOC Multi Select */}
        <h2 className="font-semibold flex">Select Warehouse</h2>
        <WarehouseSearchField
          disabled={disabled}
          type="internal"
          value={data?.warehouse}
          onSelect={(d) => {
            setData((old: any) => {
              return { ...old, warehouse: d, warehouse_id: d.id };
            });
          }}
        />

        {/* Role Select */}
        <h2 className="font-semibold flex">Select SPOC Role</h2>
        <select
          id="warehouse-management-spoc-role-select"
          className="w-full border p-2 rounded"
          value={data.role}
          onChange={(e) =>
            setData((prev) => ({
              ...prev,
              role: e.target.value,
            }))
          }
          disabled={true}
        >
          <option value="">Select Role</option>

          {Object.entries(warehouseRoles || {}).map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>

        <h2 className="font-semibold flex">Select Spocs</h2>
        <EmployeeMultiSearchWidget
          placeholder="Add Spocs"
          selectedEmployees={selectedEmployees}
          onSelect={handleSelect}
          onDelete={handleDelete}
        />

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            id="warehouse-management-modal-cancel-btn"
            onClick={onClose}
            className="px-4 py-2 border rounded"
          >
            Cancel
          </button>

          <button
            id="warehouse-management-modal-submit-btn"
            disabled={!data?.warehouse?.id || isSubmitting}
            onClick={() => onSubmit(data)}
            className={`px-4 py-2 bg-blue-600 text-white rounded transition-all min-w-[80px] flex items-center justify-center ${
              !data?.warehouse?.id || isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-blue-700"
            }`}
          >
            {isSubmitting
              ? disabled
                ? "Updating..."
                : "Assigning..."
              : disabled
                ? "Update"
                : "Assign"}
          </button>
        </div>
      </div>
      {isSubmitting && <LoadingWidget />}
    </div>
  );
};
